/**
 * Covariance with its working table: each pair's deviations from the two means and
 * their product, whose sum divided by n − 1 (sample) or n (population) is the covariance.
 */

import { mean } from "@/lib/stats/descriptive";

export interface DeviationProductRow {
  x: number;
  y: number;
  dx: number;
  dy: number;
  product: number;
}

export interface CovarianceReport {
  n: number;
  meanX: number;
  meanY: number;
  rows: DeviationProductRow[];
  /** Σ(x − x̄)(y − ȳ). */
  sumProducts: number;
  sample: number;
  population: number;
  /** Sample SDs, for the correlation that rescales the covariance. */
  sdX: number;
  sdY: number;
  /** Pearson's r = sample covariance / (sₓ × s_y); NaN when either SD is zero. */
  r: number;
}

export function covarianceReport(
  xs: readonly number[],
  ys: readonly number[]
): CovarianceReport | null {
  const n = xs.length;
  if (n < 2 || ys.length !== n) {
    return null;
  }
  const meanX = mean(xs);
  const meanY = mean(ys);
  const rows: DeviationProductRow[] = [];
  let sumProducts = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    const product = dx * dy;
    rows.push({ x: xs[i], y: ys[i], dx, dy, product });
    sumProducts += product;
    sxx += dx * dx;
    syy += dy * dy;
  }
  const sdX = Math.sqrt(sxx / (n - 1));
  const sdY = Math.sqrt(syy / (n - 1));
  const sample = sumProducts / (n - 1);
  return {
    n,
    meanX,
    meanY,
    rows,
    sumProducts,
    sample,
    population: sumProducts / n,
    sdX,
    sdY,
    r: sdX > 0 && sdY > 0 ? sample / (sdX * sdY) : Number.NaN,
  };
}
